import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import { MessageSquare, X, Minus, Maximize2, FolderOpen, GitBranch, BookOpen, Cpu, BarChart3, CheckCircle2, AlertCircle, CircleDashed, Workflow, ShieldCheck, Plus, GraduationCap, Brain } from "lucide-react";
import { loadProjectGuides } from "./tutorial/guides";
// The app view's speed dial renders the SERVER's effective companion list —
// GET /api/apps/resolve returns `companions`, computed once by
// shared/viewer-companions.js from the owner's Viewer options. This dial used
// to hardcode Chat/Pipeline/Governance and data-probe Workflows/Guides while
// reading viewer_chrome not at all, which is why a companion toggled OFF
// (Workflows) still appeared here and one toggled ON (Guides) did not.
import { resolveApp } from "../services/apps";
import { companionsFrom } from "../services/companions";
import { getTopZ } from "./zOrder";
import { createPageUrl } from "@/utils";
import { useTheme } from "@/contexts/ThemeContext";
import { useAuth } from "@/contexts/AuthContext";
import { useProjectMaybe } from "@/contexts/ProjectContext";
import { currentProjectSlug, currentBranch } from "../utils/projectPath";
import { useUiView } from "@/hooks/useUiView";

const BACKEND_URL = import.meta.env.VITE_BACKEND_ADDRESS || "/";
// The assistant is served at /assistant/* (Vite proxy in dev, ingress path in prod).
// chat.html opens WS + SSE at relative paths under this prefix.
const ASSISTANT_BASE = (import.meta.env.VITE_ASSISTANT_BASE || "/assistant/").replace(/\/?$/, "/");
// Single-container consolidation: the engine + analyst personas are now hosted
// inside the assistant container (one WS/SSE + all MCP routes), so their chat
// bases default to ASSISTANT_BASE. The env overrides remain for split-container
// dev setups, but in dev/prod all personas talk to one container.
const FLOW_ASSISTANT_BASE = (import.meta.env.VITE_FLOW_ASSISTANT_BASE || ASSISTANT_BASE).replace(/\/?$/, "/");
const ANALYST_BASE = (import.meta.env.VITE_ANALYST_BASE || ASSISTANT_BASE).replace(/\/?$/, "/");

const HARNESS_CONFIG = {
  // The single user-facing chat. Runs in the assistant container (its
  // /mcp/orch route serves delegate_to_<persona> tools) and routes each task
  // to the governance/engine/analyst sub-agents, which keep their full
  // capabilities. Replaces the three separate harness chats.
  orchestrator: {
    base: ASSISTANT_BASE,
    label: "Cortex",
    right: 24,
    icon: Cpu,
    buttonClass: "bg-nav-active hover:bg-nav-active-dark",
    titleBarColor: "#3c5d88",
  },
  engine: {
    base: FLOW_ASSISTANT_BASE,
    label: "Pipeline engine",
    right: 76,
    icon: Cpu,
    buttonClass: "bg-purple-600 hover:bg-purple-700",
    titleBarColor: "#6d28d9",
    // No standing launcher button: the engine harness opens only when the
    // orchestrator or the "Add strategy" wizard hands off to it.
    hideButton: true,
  },
  governance: {
    base: ASSISTANT_BASE,
    label: "Data dictionary",
    right: 76,
    icon: BookOpen,
    buttonClass: "bg-nav-active hover:bg-nav-active-dark",
    titleBarColor: "#3c5d88",
  },
  analyst: {
    base: ANALYST_BASE,
    label: "Data analyst",
    right: 128,
    icon: BarChart3,
    buttonClass: "bg-green-600 hover:bg-green-700",
    titleBarColor: "#15803d",
  },
};

const MIN_W = 560;  // ~70 monospace cols at 12px — narrower than this
                    // causes Ink's row count to drift from xterm's row
                    // count (because xterm hard-wraps long markdown
                    // lines), and the streaming assistant text smears
                    // down the screen instead of redrawing in place.
const MIN_H = 400;
const DEFAULT_W = 720;  // ~90 cols, comfortable default.
const DEFAULT_H = 600;

import { isPortal } from "../utils/portal";

export default function AssistantChat({ harness = "governance", hideLauncher = false }) {
  // PORTAL (standalone deployment): the REAL chat UI (chat.html iframe, same
  // as the platform), pointed at the IN-APP agent runtime — the portal nginx
  // proxies /assistant/* to agent-chat, which implements the chat protocol on
  // headless Claude Code (docs/portal-chat-protocol.md). One instance only:
  // Layout mounts AssistantChat per harness; the orchestrator one wins.
  if (isPortal()) return harness === "orchestrator" ? <AssistantChatCortex harness="orchestrator" /> : null;
  return <AssistantChatCortex harness={harness} hideLauncher={hideLauncher} />;
}

function AssistantChatCortex({ harness = "governance", hideLauncher = false }) {
  const harnessConfig = HARNESS_CONFIG[harness] || HARNESS_CONFIG.governance;
  const HarnessIcon = harnessConfig.icon;
  const [endpointStatus, setEndpointStatus] = useState(null); // engine only
  const [showEndpointPanel, setShowEndpointPanel] = useState(false);
  const { theme } = useTheme();
  const { user } = useAuth();
  // Active project + branch come from the URL (single source of truth).
  // Provider-optional: the share page mounts this chat OUTSIDE ProjectProvider
  // (deliberately — see main.jsx), so fall back to the URL, which is the single
  // source of truth for project + branch anyway.
  const projCtx = useProjectMaybe();
  const activeProject = projCtx?.projectSlug ?? currentProjectSlug();
  const activeBranch = projCtx?.branch ?? currentBranch();
  // Simple view: chat.html renders the Claude-Desktop-style shell (History
  // rail, under-input model picker, "/" typeahead) for the orchestrator.
  // Threaded via chat-config; chat.html also self-seeds from localStorage.
  const { simple } = useUiView();
  // Standard-mode chat = the orchestrator window in the simple view. Drives
  // the left-side launcher/window placement and the auto-open below.
  const standardChat = simple && harness === "orchestrator" && !isPortal();
  // Without an active project the assistant has nothing to bind to —
  // hide the toggle button entirely so we can't open a session that
  // would route to no Flow fork.
  const hasProject = !!activeProject;
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  // Latch: true once the chat has been opened at all in this page load. The
  // window (and its iframe) STAYS MOUNTED from then on — closing or collapsing
  // only HIDES it. Unmounting the iframe tears down the WS/SSE and the whole
  // transcript, which is what used to lose the session on close→open and
  // collapse→expand. Before the first open nothing is mounted, so users who
  // never touch the chat pay nothing.
  const [everOpened, setEverOpened] = useState(false);
  useEffect(() => { if (isOpen) setEverOpened(true); }, [isOpen]);
  const [size, setSize] = useState({ w: DEFAULT_W, h: DEFAULT_H });
  const [pos, setPos] = useState(null);
  // Standard mode: one-click maximize (near-fullscreen) ⇄ restore. Advanced
  // keeps drag-resize only. prevBounds remembers the floating geometry.
  const [isMaximized, setIsMaximized] = useState(false);
  const prevBoundsRef = useRef(null);
  // Theme for the iframe URL, FROZEN once the iframe mounts: a live `theme` in
  // src would reload the iframe and lose the chat session. The iframe now
  // mounts once and stays, so this only paints the FIRST load correctly —
  // every later change reaches the live iframe via chat-set-theme below.
  const iframeThemeRef = useRef(theme);
  useEffect(() => {
    if (!everOpened) iframeThemeRef.current = theme;
  }, [theme, everOpened]);
  const [zIndex, setZIndex] = useState(() => getTopZ());
  const iframeRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  // THE OPEN APP's view name, from the URL — /apps/<view> in cortex and
  // /share/apps/<view> on a share link both match, and both are live cortex.
  // Two consumers, one parse:
  //  - the speed dial below, which resolves this app's server-computed
  //    `companions` and renders them verbatim;
  //  - the APP-EMBEDDED CHAT, which sends this name to the assistant (chat-config
  //    → the WS `app` param → X-Cortex-App-View) so it can resolve the app's
  //    Viewer options ITSELF and hand the orchestrator only the personas the
  //    owner enabled. We pass the NAME and nothing else: what it unlocks is
  //    never a client-side decision (assistant/companion-gate.js).
  // Declared HERE, right after `location` — reading it above the useLocation()
  // call is a TDZ error that blanks every page mounting this component.
  const appViewName = decodeURIComponent((location.pathname.match(/\/apps\/([^/?#]+)/) || [])[1] || "");
  // WHICH chat the open app publishes — 'cortex' | 'analyst' | null (not an app
  // view, or the resolve has not answered). Filled from GET /api/apps/resolve's
  // `chat_kind` by the dial effect further down, and threaded into chat-config
  // so the panel titles itself correctly and chat.html asks for the analyst
  // persona's default model. It never DECIDES anything: the ws handler reads
  // the app row and picks the harness server-side, so a tampered value changes
  // a caption, not a capability.
  const [dialChatKind, setDialChatKind] = useState(null);
  // …and the same value as a REF, for exactly the reason selectedAgentRef
  // exists: the chat-ready handler's effect does NOT re-subscribe when the kind
  // changes, so reading the state inside it sees the pre-resolve closure (null)
  // and the first config a fresh iframe receives would carry no kind at all.
  // The ref is always current, so chat-ready delivers the right kind whenever
  // the resolve has landed, and the push effect below covers the case where it
  // lands afterwards. Exactly one of the two paths posts per change.
  const dialChatKindRef = useRef(null);
  // True while the chat-ready handshake is HELD waiting for the app resolve
  // to say WHICH chat this panel is. Posting the first config before the kind
  // is known opens a session in the wrong harness that the late-arriving kind
  // must reset — and a message typed in that gap goes into the dying session
  // and is silently lost (HANDOFF-2026-08-31 §2). The push effect below posts
  // the held config the moment the kind lands.
  const chatReadyHeldRef = useRef(false);
  // How this window presents itself. An app published with the ANALYST chat is
  // not "Cortex" — calling it that would promise the orchestrator's reach over a
  // panel that only answers data questions, which is precisely the mismatch the
  // Viewer-options sub-choice exists to remove. The chrome follows the owner's
  // choice; the CAPABILITY follows the server's read of the same app row.
  // Scoped to the ORCHESTRATOR window: that is the one an app view mounts, and
  // the only one the kind applies to. The engine window can be open over the
  // same app (the "Add strategy" handoff) and is a different chat entirely —
  // retitling it from an app's Chat option would be simply wrong.
  const chatConfig = (harness === "orchestrator" && dialChatKind === "analyst")
    ? HARNESS_CONFIG.analyst : harnessConfig;
  const ChatIcon = chatConfig.icon;
  // A prompt to seed into the chat input on next open — set by the
  // `cortex:open-harness` event (e.g. the "Add strategy" wizard handoff).
  const [pendingPrompt, setPendingPrompt] = useState(null);
  // A project agent to SWITCH the chat to — set by the `cortex:open-harness`
  // event from the Agents page (clicking an agent row). Threaded to the iframe
  // via chat-config → ws → prompt injection so the orchestrator routes to it,
  // and chat.html pins a chip. { name, display_name, icon }.
  //
  // Two representations, deliberately:
  //  - selectedAgent (state)     — triggers the "agent changed while open"
  //    push effect below.
  //  - selectedAgentRef (ref)    — the always-current value, updated
  //    synchronously in the event handler and read by the chat-ready config
  //    handler. The chat-ready listener's effect does NOT re-subscribe on
  //    agent changes, so reading the state there would see a stale closure
  //    (the pre-click null) — the ref is what guarantees the FIRST config a
  //    fresh iframe receives already carries the just-clicked agent.
  const [selectedAgent, setSelectedAgent] = useState(null);
  const selectedAgentRef = useRef(null);
  // True only after a chat-config was ACTUALLY posted to the currently
  // mounted, handshook iframe (set in the chat-ready handler, cleared when
  // the iframe unmounts via close/minimize). The push effect uses it to
  // decide who delivers an agent change: if no config has reached this
  // iframe yet, chat-ready will carry the agent (via the ref) — pushing
  // early would hit a not-yet-listening document and be lost.
  const configDeliveredRef = useRef(false);

  // Initialize position on first open: bottom-right normally; Standard mode
  // opens bottom-LEFT of the content area (past the 256px sidebar) so the
  // window grows out of its left-side launcher.
  useEffect(() => {
    if (isOpen && pos === null) {
      setPos({
        x: standardChat
          ? Math.max(8, Math.min(280, window.innerWidth - DEFAULT_W - 24))
          : window.innerWidth - DEFAULT_W - 24,
        y: window.innerHeight - DEFAULT_H - 24,
      });
    }
  }, [isOpen, pos, standardChat]);

  // Standard mode: the chat is the product's front door — auto-open it on
  // first entry (any page), once per tab session. A deliberate close (✕ or
  // an in-chat exit) sets the flag so it stays closed until a new tab/visit.
  useEffect(() => {
    if (!standardChat || !hasProject) return;
    try {
      if (sessionStorage.getItem("cortex_chat_dismissed") === "1") return;
    } catch (_) { /* storage unavailable — still auto-open */ }
    setIsOpen(true);
  }, [standardChat, hasProject]);
  const closeChat = useCallback(() => {
    if (harness === "orchestrator") {
      try { sessionStorage.setItem("cortex_chat_dismissed", "1"); } catch (_) { /* best effort */ }
    }
    setIsOpen(false);
  }, [harness]);

  // Cross-component handoff: another page (e.g. the Strategies "Add strategy"
  // button) can request THIS harness be opened with a seeded prompt. Only the
  // instance whose `harness` matches reacts. The engine harness is where new
  // strategy kinds get authored (governance/analyst can't touch Flow).
  useEffect(() => {
    const onOpenHarness = (e) => {
      const d = e.detail || {};
      if (d.harness && d.harness !== harness) return;
      if (d.initialPrompt) {
        setPendingPrompt(d.initialPrompt);
        // An ALREADY-configured iframe never re-runs the chat-ready handshake,
        // so the pending prompt would sit undelivered. Seed it live — a
        // dedicated lightweight message, deliberately NOT a full chat-config:
        // this effect only re-subscribes on `harness`, and re-sending config
        // built from stale closures could re-point the chat's project.
        const win = iframeRef.current?.contentWindow;
        if (win && configDeliveredRef.current) {
          win.postMessage({ type: "chat-seed-prompt", prompt: d.initialPrompt }, "*");
          setPendingPrompt(null);
        }
      }
      // A selected agent switches the chat to that agent (Agents page row
      // click). Update the ref SYNCHRONOUSLY so any chat-ready that fires
      // before React re-renders already sees the new agent; the state update
      // triggers the live-iframe push effect below. null is preserved for
      // the no-agent flows so nothing changes for other callers.
      if ("selectedAgent" in d) {
        selectedAgentRef.current = d.selectedAgent || null;
        setSelectedAgent(d.selectedAgent || null);
      }
      setIsOpen(true);
      setIsMinimized(false);
    };
    window.addEventListener("cortex:open-harness", onOpenHarness);
    return () => window.removeEventListener("cortex:open-harness", onOpenHarness);
  }, [harness]);

  // Send config to iframe when it's ready
  useEffect(() => {
    const handler = (e) => {
      if (e.data?.type === "chat-ready" && iframeRef.current) {
        // APP-EMBEDDED orchestrator chat whose kind the resolve has not
        // delivered yet: HOLD the config. Delivering now would open a session
        // the resolve's answer immediately resets (chat.html mints a fresh
        // session on a harness change), and anything typed in between dies
        // with the first session. The push effect posts the held config when
        // dialChatKind lands; the timer is the resolve-never-answers fallback
        // (kind defaults to 'cortex', the server enforces the truth anyway).
        if (harness === "orchestrator" && appViewName && !isPortal() && !dialChatKindRef.current) {
          chatReadyHeldRef.current = true;
          setTimeout(() => {
            if (chatReadyHeldRef.current && !dialChatKindRef.current) {
              dialChatKindRef.current = "cortex";
              setDialChatKind("cortex");
            }
          }, 4000);
          return;
        }
        iframeRef.current.contentWindow.postMessage({
          type: "chat-config",
          backendUrl: harnessConfig.base,
          token: localStorage.getItem("access_token") || "",
          currentPage: location.pathname + location.search,
          activeProject,
          activeBranch,
          harness,
          simple,
          theme,
          // Seed an initial prompt into the input (e.g. add-strategy handoff).
          initialPrompt: pendingPrompt || undefined,
          // Switch the chat to a project agent (Agents page row click). Read
          // from the REF, not the state: this effect doesn't re-subscribe on
          // agent changes, so the state here can be a stale closure — the ref
          // is always current. null/undefined = no agent (default — zero
          // behavior change for every other caller).
          selectedAgent: selectedAgentRef.current || undefined,
          // The app this chat is a companion of ('' off an app view) — decides
          // the persona roster server-side.
          appView: appViewName || undefined,
          // …and WHICH chat that app publishes ('analyst' ⇒ chat.html asks for
          // the analyst harness and its default model). The server enforces the
          // same choice from the app row regardless of what arrives here.
          chatKind: dialChatKindRef.current || undefined,
        }, "*");
        // Only now has a config actually reached this iframe — the agent
        // push effect may deliver subsequent agent changes directly.
        configDeliveredRef.current = true;
        if (pendingPrompt) setPendingPrompt(null);
      }
      if (e.data?.type === "chat-close") closeChat();
      if (e.data?.type === "chat-navigate") navigate(e.data.path);
      // Open a project agent's companion app in the main cortex window. The
      // chat sends the app's *.view endpoint name (== the app view_name); we
      // navigate to it the same way the Apps page does (project-prefixed URL).
      if (e.data?.type === "chat-open-app" && e.data.viewName) {
        navigate(createPageUrl(`apps/${encodeURIComponent(e.data.viewName)}`));
      }
      // A SAVED GUIDE launch (chat rail): Layout registers + starts the tour;
      // this window only gets out of the spotlight's way.
      if (e.data?.type === "cortex:launch-guide") setIsMinimized(true);
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [navigate, location, activeProject, activeBranch, pendingPrompt, harness, harnessConfig.base, simple, theme, closeChat]);

  // NOTE: the iframe used to unmount on close AND on minimize, so the
  // delivered-config flag was cleared here to force a fresh handshake. It now
  // survives both (that unmount is exactly what lost the session), so the flag
  // must NOT be cleared — the same live iframe keeps its config, and
  // chat-ready re-arms it if the iframe ever does reload on its own.

  // When the agent changes while the chat iframe is already LIVE (mounted +
  // handshook + configured), chat-ready won't fire again — push a fresh full
  // config so chat.html can switch the session. If the iframe isn't live yet
  // (closed, mounting, or handshaking), do NOTHING here: the chat-ready
  // handler above reads selectedAgentRef and delivers the agent in the first
  // config. Exactly one of the two paths posts per agent change — this effect
  // never marks anything consumed without actually posting, so no click is
  // ever swallowed.
  useEffect(() => {
    const win = iframeRef.current?.contentWindow;
    if (!win) return;
    if (!configDeliveredRef.current) {
      // chat-ready path owns first delivery — EXCEPT when it held the config
      // waiting for the app resolve's chatKind. Release the hold now that the
      // kind is known (dialChatKind is a dep), or when navigation left the app
      // while the hold was pending (no kind will ever come then).
      if (!(chatReadyHeldRef.current && (dialChatKindRef.current || !appViewName))) return;
      chatReadyHeldRef.current = false;
    }
    win.postMessage({
      type: "chat-config",
      backendUrl: harnessConfig.base,
      token: localStorage.getItem("access_token") || "",
      currentPage: location.pathname + location.search,
      activeProject,
      activeBranch,
      harness,
      simple,
      theme,
      selectedAgent: selectedAgentRef.current || undefined,
      appView: appViewName || undefined,
      chatKind: dialChatKindRef.current || undefined,
    }, "*");
    configDeliveredRef.current = true;
    // appViewName joins the deps: walking from one app to another (or out of an
    // app) changes which personas the session may carry, and chat.html mints a
    // fresh session on an app change exactly as it does on an agent change.
    // dialChatKind joins it for the same reason one step later: the resolve
    // answers AFTER the first config is delivered, so without this dep a chat
    // opened on an analyst app would keep the orchestrator's title and model
    // default until the next navigation.
  }, [selectedAgent, simple, appViewName, dialChatKind]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch endpoint readiness for the engine harness when chat opens.
  // This is the harness's responsibility: list which cortex.* endpoints
  // exist on the paired Flow fork so the user knows what's left to author.
  useEffect(() => {
    if (harness !== "engine" || !isOpen || !activeProject) return;
    const token = localStorage.getItem("access_token") || "";
    const url = `${harnessConfig.base}endpoints/status?project=${encodeURIComponent(activeProject)}&token=${encodeURIComponent(token)}`;
    let cancelled = false;
    fetch(url)
      .then((r) => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then((data) => { if (!cancelled) setEndpointStatus(data); })
      .catch((err) => { if (!cancelled) setEndpointStatus({ error: err.message }); });
    return () => { cancelled = true; };
  }, [harness, isOpen, activeProject, harnessConfig.base]);

  // Update context when location/project/branch changes. Keyed on `everOpened`,
  // not `isOpen`: a hidden (closed/collapsed) chat is still MOUNTED and must
  // keep tracking the page, so reopening it shows the current context instead
  // of wherever the user was when they closed it.
  useEffect(() => {
    if (everOpened && iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage({
        type: "chat-update-context",
        currentPage: location.pathname + location.search,
        token: localStorage.getItem("access_token") || "",
        activeProject,
        activeBranch,
      }, "*");
    }
  }, [location, everOpened, activeProject, activeBranch]);

  // Same reasoning for theme: push to the live iframe whenever it exists, so a
  // theme toggled while the chat is hidden is already applied on reopen.
  useEffect(() => {
    if (everOpened && iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage({ type: "chat-set-theme", theme }, "*");
    }
  }, [theme, everOpened]);

  // Drag to move via title bar.
  //
  // POINTER CAPTURE, not document mouse listeners: releasing the button over
  // ANY iframe (the app embed on the share page covers most of the screen)
  // sent mouseup into that iframe's document, the parent never saw it, and the
  // gesture stayed armed — the panel then kept moving/resizing on bare hover.
  // Capturing the pointer on the handle guarantees up/cancel delivery no
  // matter what is under the cursor; the e.buttons===0 guard self-heals if a
  // capture is ever lost (e.g. the browser broke it on a context switch).
  const handleDragStart = useCallback((e) => {
    e.preventDefault();
    const handle = e.currentTarget;
    try { handle.setPointerCapture(e.pointerId); } catch {}
    const startX = e.clientX - pos.x;
    const startY = e.clientY - pos.y;
    const onMove = (ev) => {
      if (ev.buttons === 0) return onUp(ev);
      setPos({
        x: Math.max(0, Math.min(window.innerWidth - size.w, ev.clientX - startX)),
        y: Math.max(0, Math.min(window.innerHeight - 48, ev.clientY - startY)),
      });
    };
    const onUp = (ev) => {
      try { handle.releasePointerCapture(ev.pointerId); } catch {}
      handle.removeEventListener("pointermove", onMove);
      handle.removeEventListener("pointerup", onUp);
      handle.removeEventListener("pointercancel", onUp);
      document.body.style.userSelect = "";
      if (iframeRef.current) iframeRef.current.style.pointerEvents = "";
    };
    document.body.style.userSelect = "none";
    if (iframeRef.current) iframeRef.current.style.pointerEvents = "none";
    handle.addEventListener("pointermove", onMove);
    handle.addEventListener("pointerup", onUp);
    handle.addEventListener("pointercancel", onUp);
  }, [pos, size.w]);

  // Resize via edges/corners — same pointer-capture rules as the drag above.
  const handleResizeStart = useCallback((e, direction) => {
    e.preventDefault();
    e.stopPropagation();
    const handle = e.currentTarget;
    try { handle.setPointerCapture(e.pointerId); } catch {}
    const startX = e.clientX, startY = e.clientY;
    const startW = size.w, startH = size.h;
    const startPosX = pos.x, startPosY = pos.y;
    const onMove = (ev) => {
      if (ev.buttons === 0) return onUp(ev);
      const dx = ev.clientX - startX, dy = ev.clientY - startY;
      let newW = startW, newH = startH, newX = startPosX, newY = startPosY;
      if (direction.includes("e")) newW = Math.max(MIN_W, startW + dx);
      if (direction.includes("w")) { newW = Math.max(MIN_W, startW - dx); newX = startPosX + (startW - newW); }
      if (direction.includes("s")) newH = Math.max(MIN_H, startH + dy);
      if (direction.includes("n")) { newH = Math.max(MIN_H, startH - dy); newY = startPosY + (startH - newH); }
      setSize({ w: newW, h: newH });
      setPos({ x: newX, y: newY });
    };
    const onUp = (ev) => {
      try { handle.releasePointerCapture(ev.pointerId); } catch {}
      handle.removeEventListener("pointermove", onMove);
      handle.removeEventListener("pointerup", onUp);
      handle.removeEventListener("pointercancel", onUp);
      document.body.style.userSelect = "";
      if (iframeRef.current) iframeRef.current.style.pointerEvents = "";
    };
    document.body.style.userSelect = "none";
    if (iframeRef.current) iframeRef.current.style.pointerEvents = "none";
    handle.addEventListener("pointermove", onMove);
    handle.addEventListener("pointerup", onUp);
    handle.addEventListener("pointercancel", onUp);
  }, [size, pos]);

  // With an APP open (/apps/<view>) the launcher becomes a SPEED DIAL: the
  // app is the user's whole screen, so cortex's own surfaces they still need
  // mid-flow — the chat, the Pipeline canvas, Data Governance — fan out of
  // the one button and open in modals over the app (embed=1 chrome-less).
  const appOpen = /\/apps\/[^/?#]+/.test(location.pathname || "");
  const [dialOpen, setDialOpen] = useState(false);
  useEffect(() => { setDialOpen(false); }, [location.pathname]);
  // The open app's view name — parsed once, up beside `location` (the chat's
  // own wiring needs it too, and before this line).
  const dialViewName = appViewName;
  // THE COMPANION LIST for the open app, from the server. `null` until it
  // answers (and if the resolve fails or the backend is too old to send the
  // field) — the dial keeps its legacy full set in that case rather than
  // vanishing, but the moment a list arrives it is rendered EXACTLY.
  const [dialCompanions, setDialCompanions] = useState(null);
  const [dialGuideSlugs, setDialGuideSlugs] = useState(null);
  // The open app is an APP SERVICE (kind 'service'): its page carries the
  // cortex app SDK, which draws the dial INSIDE the service — the same
  // companions, from the same resolve — so this shell must not draw a second
  // one over it (2026-09-08). Views keep the shell's dial.
  const [dialServiceApp, setDialServiceApp] = useState(false);
  useEffect(() => {
    if (harness !== "orchestrator" || !appOpen || isPortal() || !dialViewName) {
      setDialCompanions(null); setDialGuideSlugs(null); setDialServiceApp(false);
      dialChatKindRef.current = null; setDialChatKind(null); return;
    }
    let alive = true;
    setDialCompanions(null);
    setDialServiceApp(false);
    resolveApp(dialViewName)
      .then((r) => {
        if (!alive) return;
        setDialServiceApp(r?.app?.kind === "service" && !!r?.app?.service_url);
        setDialCompanions(companionsFrom(r));
        setDialGuideSlugs(Array.isArray(r?.guide_slugs) ? r.guide_slugs : null);
        // WHICH chat this app's Chat companion opens (Viewer options → Chat →
        // Cortex | Analyst). Presentation only, exactly like `companions`: the
        // assistant re-reads the app row itself when the session opens and
        // picks the harness there (assistant/ws-handler.js). This copy exists
        // so the panel's TITLE and its DEFAULT MODEL match what the session
        // will actually be, instead of saying "Cortex" over an analyst chat.
        const kind = r?.chat_kind === "analyst" ? "analyst" : "cortex";
        dialChatKindRef.current = kind;   // synchronous; chat-ready reads THIS
        setDialChatKind(kind);            // triggers the push effect + retitles
      })
      .catch(() => {
        // The chat-ready handler may be HOLDING the first config for this
        // kind — a failed resolve must still release it. 'cortex' mirrors the
        // server default (the ws handler re-reads the app row regardless).
        if (!alive || dialChatKindRef.current) return;
        dialChatKindRef.current = "cortex";
        setDialChatKind("cortex");
      });
    return () => { alive = false; };
  }, [harness, appOpen, activeProject, dialViewName]);
  // Guide CONTENT (not availability — that is the list above): the tours a
  // Guides press can run. guide_slugs is the owner's per-guide narrowing, same
  // rule as the share page; absent = this app's own tours.
  const [dialGuides, setDialGuides] = useState([]);
  useEffect(() => {
    if (harness !== "orchestrator" || !appOpen || isPortal()) { setDialGuides([]); return; }
    let alive = true;
    setDialGuides([]);
    loadProjectGuides(activeProject)
      .then((tours) => {
        if (!alive) return;
        const picks = Array.isArray(dialGuideSlugs) ? new Set(dialGuideSlugs) : null;
        setDialGuides((tours || []).filter((t) =>
          picks ? picks.has(t?.__guide?.slug) : t?.__guide?.app === dialViewName
        ));
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [harness, appOpen, activeProject, dialViewName, dialGuideSlugs]);
  const openEmbed = (page, label, group) => {
    setDialOpen(false);
    window.dispatchEvent(new CustomEvent("cortex:open-embed-page", { detail: { page, label, group } }));
  };
  // The dial's items — the SERVER's companion list for the open app, mapped to
  // this surface's chrome and rendered in order. `null` (resolve not answered,
  // or an older backend) falls back to COMPANION_ORDER minus docs, the set this
  // dial shipped before — so a resolve failure degrades to the old behavior
  // rather than an empty corner.
  //
  // What is NOT here any more: the unconditional Chat/Pipeline/Governance
  // buttons and the workflows/guides data probes. A companion the owner
  // switched off cannot reach this list, which is the bug being fixed —
  // Workflows used to appear here purely because definitions existed.
  const DIAL_ITEMS = {
    chat: { label: "Chat", bg: "#3c5d88", Icon: MessageSquare,
      onClick: () => { setDialOpen(false); setIsOpen(true); } },
    workflows: { label: "Workflows", bg: "#0f172a", Icon: Workflow,
      onClick: () => openEmbed("workflows", "Workflows") },
    guides: { label: dialGuides.length === 1 ? (dialGuides[0].title || "Guide") : "Guides",
      bg: "#7c3aed", Icon: GraduationCap,
      onClick: () => {
        setDialOpen(false);
        if (dialGuides.length === 1) window.dispatchEvent(new CustomEvent("cortex:start-tutorial", { detail: { tour: dialGuides[0].key } }));
        else navigate(createPageUrl("guides"));
      } },
    models: { label: "Models", bg: "#0e7490", Icon: Brain,
      onClick: () => openEmbed("models", "Models") },
    pipeline: { label: "Pipeline", bg: "#4f46e5", Icon: GitBranch,
      onClick: () => openEmbed("pipeline", "Pipeline") },
    governance: { label: "Governance", bg: "#059669", Icon: ShieldCheck,
      onClick: () => openEmbed("data-dictionary", "Data Governance", "governance") },
    docs: { label: "Docs", bg: "#b45309", Icon: BookOpen,
      onClick: () => openEmbed("docs", "Docs") },
  };
  const dialKeys = dialCompanions || ["chat", "workflows", "guides", "pipeline", "governance"];

  // Docked bottom-RIGHT, where the chat button has always lived — the dial IS
  // the chat button now (same round blue bubble), it just fans the app's
  // enabled companions above the chat action.
  const speedDial = (
    <div className="fixed bottom-6 right-6 z-[99999] flex flex-col-reverse items-end gap-2">
      <button
        onClick={() => setDialOpen(!dialOpen)}
        data-tour="chat-launcher-orchestrator"
        data-testid="app-speed-dial"
        title={dialOpen ? "Close" : "Companions"}
        onMouseEnter={(e) => { e.currentTarget.style.opacity = "1"; }}
        onMouseLeave={(e) => { if (!dialOpen) e.currentTarget.style.opacity = "0.55"; }}
        style={{ width: 44, height: 44, background: "#3c5d88", opacity: dialOpen ? 1 : 0.55, boxShadow: "0 4px 14px rgba(0,0,0,.28)", transition: "opacity .2s" }}
        className="rounded-full flex items-center justify-center cursor-pointer border-0 text-white"
      >
        {dialOpen ? <X className="w-5 h-5 text-white" /> : <Plus className="w-5 h-5 text-white" />}
      </button>
      {dialOpen && (
        <>
          {dialKeys.map((key) => {
            const it = DIAL_ITEMS[key];
            if (!it) return null;
            return (
              <button key={key} onClick={it.onClick} data-testid={`dial-${key}`}
                style={{ background: it.bg, boxShadow: "0 4px 14px rgba(0,0,0,.28)" }}
                className="h-10 pl-3 pr-4 rounded-full flex items-center gap-2 text-white text-sm font-medium cursor-pointer border-0 hover:opacity-90 transition-all">
                <it.Icon className="w-5 h-5" /> {it.label}
              </button>
            );
          })}
        </>
      )}
    </div>
  );

  // Standard mode: the orchestrator launcher is the front door to the
  // product's chat — size it like one (56px round, chat-widget convention)
  // and dock it bottom-LEFT of the content area (past the 256px sidebar —
  // left-6 would cover the profile menu; on mobile the sidebar is an
  // overlay, so plain left-6 works). Advanced keeps the 40px bottom-right.
  const toggleButton = (
    <button
      onClick={() => setIsOpen(true)}
      style={standardChat ? undefined : { right: harnessConfig.right }}
      data-tour={`chat-launcher-${harness}`}
      className={`fixed bottom-6 z-[99999] flex items-center justify-center cursor-pointer hover:scale-105 transition-all ${
        standardChat ? "w-14 h-14 rounded-full shadow-xl left-6 md:left-[280px]" : "w-10 h-10 rounded-xl shadow-lg"
      } ${harnessConfig.buttonClass}`}
      title={`Open ${harnessConfig.label}`}
    >
      <HarnessIcon className={standardChat ? "w-7 h-7 text-white" : "w-5 h-5 text-white"} />
    </button>
  );

  if (!hasProject) return null;
  // Mobile with an app open full-screen (/apps/<view>): the floating launcher
  // sits exactly over the app's own composer/controls — stand down there.
  const mobileAppOpen = typeof window !== "undefined"
    && window.matchMedia("(max-width: 767px)").matches
    && /\/apps\/[^/?#]+/.test(location.pathname || "");
  // The launcher shown while the chat is closed (null when this harness has no
  // standing button, on mobile over a full-screen app, or when the host page
  // provides its own launcher — e.g. the share page's speed dial, which opens
  // this chat via the `cortex:open-harness` event instead).
  const closedLauncher = (harnessConfig.hideButton || mobileAppOpen || hideLauncher || (appOpen && dialServiceApp))
    ? null
    // Desktop with an app open: the speed dial replaces the chat-only button
    // for the orchestrator in BOTH ui views (simple and advanced) — the app is
    // full-screen either way and the dial is the one door back to cortex.
    : (harness === "orchestrator" && !isPortal() && appOpen ? speedDial : toggleButton);

  // CLOSED. Once the chat has been opened, we still render the window — hidden —
  // so the iframe (and with it the WS/SSE, the transcript and any half-typed
  // input) survives. Unmounting it here is what used to lose the session.
  // Before the first open there is nothing to preserve, so render just the
  // launcher.
  if (!isOpen && (!everOpened || pos === null)) {
    return closedLauncher ? createPortal(closedLauncher, document.body) : null;
  }
  if (pos === null) return null;

  // Hidden = closed but still mounted. `display: none` (not just opacity) so it
  // is out of the layout, unhittable, and its contents are removed from the
  // accessibility tree and the tab order.
  const hidden = !isOpen;
  const windowH = isMinimized ? 48 : size.h;

  const chatWindow = (
    <div
      onMouseDown={() => setZIndex(getTopZ())}
      data-chat-window={harness}
      aria-hidden={hidden ? "true" : undefined}
      className="fixed flex flex-col overflow-hidden rounded-xl border border-[#C8D9EC] shadow-[0_8px_40px_0_var(--shadow-blue-md)]"
      style={{ display: hidden ? "none" : undefined, left: pos.x, top: pos.y, width: size.w, height: windowH, minWidth: MIN_W, minHeight: isMinimized ? 48 : MIN_H, zIndex }}
    >
      {/* Title bar */}
      <div
        onPointerDown={handleDragStart}
        style={{ backgroundColor: chatConfig.titleBarColor }}
        className="flex items-center gap-2 px-4 h-12 cursor-grab active:cursor-grabbing select-none flex-shrink-0"
      >
        <ChatIcon className="w-4 h-4 shrink-0 text-white/70" data-testid="app-chat-icon" />
        <span className="text-sm font-medium truncate text-white/90 mr-2" data-testid="app-chat-title">{chatConfig.label}</span>
        {/* Simple view keeps the title bar bare (Claude-Desktop style) — the
            project lives in the chat's own status line, the branch is git. */}
        {!(simple && harness === "orchestrator") && (
          <>
            <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-white/10 text-white/80">
              <FolderOpen className="w-3 h-3" /> {activeProject}
            </span>
            <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-white/10 text-white/80">
              <GitBranch className="w-3 h-3" /> {activeBranch}
            </span>
          </>
        )}
        {harness === "engine" && endpointStatus && !endpointStatus.error && (
          <button
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => setShowEndpointPanel((v) => !v)}
            className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border-0 cursor-pointer transition-colors ${
              endpointStatus.missing === 0 && endpointStatus.errored === 0
                ? "bg-green-500/20 text-green-100 hover:bg-green-500/30"
                : "bg-amber-500/20 text-amber-100 hover:bg-amber-500/30"
            }`}
            title="Implemented endpoints on paired Flow fork"
          >
            <CheckCircle2 className="w-3 h-3" />
            {endpointStatus.implemented}/{endpointStatus.total} endpoints
          </button>
        )}
        <span className="flex-1" />
        {simple && harness === "orchestrator" && !isMinimized && (
          <button
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => {
              if (!isMaximized) {
                prevBoundsRef.current = { pos, size };
                setPos({ x: 16, y: 16 });
                setSize({ w: window.innerWidth - 32, h: window.innerHeight - 32 });
                setIsMaximized(true);
              } else {
                const b = prevBoundsRef.current;
                if (b) { setPos(b.pos); setSize(b.size); }
                setIsMaximized(false);
              }
            }}
            className="w-6 h-6 flex items-center justify-center rounded border-0 bg-transparent cursor-pointer text-white/60 hover:text-white transition-colors"
            title={isMaximized ? "Restore size" : "Maximize"}
          >
            <Maximize2 className="w-3.5 h-3.5" />
          </button>
        )}
        <button
          onPointerDown={(e) => e.stopPropagation()}
          onClick={() => setIsMinimized((v) => !v)}
          className="w-6 h-6 flex items-center justify-center rounded border-0 bg-transparent cursor-pointer text-white/60 hover:text-white transition-colors"
          title={isMinimized ? "Restore" : "Minimize"}
        >
          {isMinimized ? <Maximize2 className="w-3.5 h-3.5" /> : <Minus className="w-3.5 h-3.5" />}
        </button>
        <button
          onPointerDown={(e) => e.stopPropagation()}
          onClick={closeChat}
          className="w-6 h-6 flex items-center justify-center rounded border-0 bg-transparent cursor-pointer text-white/60 hover:text-white transition-colors"
          title="Close"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* iframe — mounted ONCE and never torn down by close/collapse. Collapsing
          hides it with CSS instead of unmounting, because unmounting kills the
          WS/SSE and the transcript (the close→open / collapse→expand data loss).
          `hidden` on the wrapping window already covers the closed case; this
          handles the collapsed one. */}
      <iframe
        ref={iframeRef}
        src={`/chat.html?v=${typeof __BUILD_ID__ !== 'undefined' ? __BUILD_ID__ : 'dev'}&theme=${iframeThemeRef.current}`}
        className="flex-1 border-0 bg-card-bg"
        style={isMinimized ? { display: "none" } : undefined}
        title="AI Assistant"
      />

      {/* Endpoint readiness popover (engine harness) */}
      {!isMinimized && harness === "engine" && showEndpointPanel && endpointStatus && !endpointStatus.error && (
        <div className="absolute top-12 left-0 right-0 bottom-0 bg-white dark:bg-slate-900 z-10 overflow-auto p-4 text-sm border-t border-slate-200 dark:border-slate-700">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="font-semibold text-slate-900 dark:text-slate-100">
                Paired Flow fork endpoint contract
              </div>
              <div className="text-xs text-slate-500 dark:text-slate-400">
                Endpoints downstream harnesses (engine, analyst, governance UI) expect on this fork.
                Status reflects the live fork; origin shows whether the endpoint is inherited from core,
                fork-specific, or still needs authoring.
              </div>
              {endpointStatus.fork_project_id && (
                <div className="text-[10px] text-slate-400 font-mono mt-1">
                  fork: {endpointStatus.fork_project_id}
                  {endpointStatus.core_project_id && <> · core: {endpointStatus.core_project_id}</>}
                </div>
              )}
            </div>
            <button
              onClick={() => setShowEndpointPanel(false)}
              className="w-6 h-6 flex items-center justify-center rounded border-0 bg-transparent cursor-pointer text-slate-500 hover:text-slate-900 dark:hover:text-slate-100"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="mb-3 flex flex-wrap gap-3 text-xs">
            <span className="inline-flex items-center gap-1 text-green-700 dark:text-green-400">
              <CheckCircle2 className="w-3.5 h-3.5" /> {endpointStatus.implemented} implemented
            </span>
            <span className="inline-flex items-center gap-1 text-slate-500">
              <CircleDashed className="w-3.5 h-3.5" /> {endpointStatus.missing} missing
            </span>
            {endpointStatus.errored > 0 && (
              <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400">
                <AlertCircle className="w-3.5 h-3.5" /> {endpointStatus.errored} errored
              </span>
            )}
            {endpointStatus.by_origin && Object.keys(endpointStatus.by_origin).length > 0 && (
              <span className="text-slate-400 ml-2">·</span>
            )}
            {endpointStatus.by_origin?.inherited > 0 && (
              <span className="inline-flex items-center gap-1 text-blue-700 dark:text-blue-400" title="Inherited from core (no fork-side authoring needed)">
                {endpointStatus.by_origin.inherited} inherited
              </span>
            )}
            {endpointStatus.by_origin?.fork_only > 0 && (
              <span className="inline-flex items-center gap-1 text-purple-700 dark:text-purple-400" title="Fork-only (project-specific)">
                {endpointStatus.by_origin.fork_only} fork-only
              </span>
            )}
            {endpointStatus.by_origin?.none > 0 && (
              <span className="inline-flex items-center gap-1 text-amber-700 dark:text-amber-400" title="Needs authoring on the fork">
                {endpointStatus.by_origin.none} unauthored
              </span>
            )}
          </div>
          {/* Group endpoints by harness for the listing. */}
          {(() => {
            // Origin → presentation
            const ORIGIN_META = {
              inherited:  { label: "from core",   color: "text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30" },
              fork_only:  { label: "fork-only",   color: "text-purple-700 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/30" },
              fork:       { label: "fork",        color: "text-purple-700 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/30" },
              none:       { label: "unauthored",  color: "text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30" },
              core_only:  { label: "core only",   color: "text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/30" },
              unknown:    { label: "unknown",     color: "text-slate-500 bg-slate-100 dark:bg-slate-800" },
            };
            // Split shipped (real contract) from planned (referenced but
            // never built). Shown in two separate sections so a "missing
            // planned" entry isn't confused with a broken integration.
            const shippedEndpoints = endpointStatus.endpoints.filter((ep) => ep.tier !== "planned");
            const plannedEndpoints = endpointStatus.endpoints.filter((ep) => ep.tier === "planned");
            const groups = {};
            for (const ep of shippedEndpoints) {
              const h = ep.harness || "engine";
              if (!groups[h]) groups[h] = [];
              groups[h].push(ep);
            }
            const HARNESS_LABEL = {
              engine: "Engine — pipeline orchestration, source/target/rule adapters",
              analyst: "Analyst — data inspection surface",
              governance: "Governance UI ops",
            };
            const harnessOrder = ["engine", "governance", "analyst"];
            const shippedSections = harnessOrder.filter((h) => groups[h]).map((h) => (
              <div key={h} className="mb-4">
                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
                  {HARNESS_LABEL[h] || h}
                </div>
                <ul className="space-y-1">
                  {groups[h].map((ep) => {
                    const Icon = ep.status === "implemented" ? CheckCircle2 : ep.status === "errored" ? AlertCircle : CircleDashed;
                    const statusColor = ep.status === "implemented" ? "text-green-600 dark:text-green-400" : ep.status === "errored" ? "text-red-600 dark:text-red-400" : "text-slate-400";
                    const om = ORIGIN_META[ep.origin] || ORIGIN_META.unknown;
                    return (
                      <li key={ep.name} className="flex items-start gap-2 py-1">
                        <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${statusColor}`} />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="font-mono text-xs text-slate-900 dark:text-slate-100">{ep.name}</span>
                            <span className={`text-[9px] px-1 py-0.5 rounded ${om.color}`} title={ep.origin_note || om.label}>
                              {om.label}
                            </span>
                          </div>
                          <div className="text-[11px] text-slate-500 dark:text-slate-400">{ep.purpose}</div>
                          {ep.origin_note && ep.status !== "implemented" && (
                            <div className="text-[10px] text-slate-500 italic mt-0.5">{ep.origin_note}</div>
                          )}
                          {ep.error && (
                            <div className="text-[11px] text-red-600 dark:text-red-400 font-mono mt-0.5 break-all">{ep.error}</div>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ));
            // Planned bucket — surface as a separate section so its
            // "missing" entries don't confuse the user about broken
            // features. These are gaps the engine harness can plan.
            const plannedSection = plannedEndpoints.length > 0 ? (
              <div key="planned" className="mb-4 mt-6 pt-4 border-t border-slate-200 dark:border-slate-700">
                <div className="text-[11px] uppercase tracking-wider text-amber-700 dark:text-amber-400 font-semibold mb-1">
                  Planned (referenced in code, not yet authored)
                </div>
                <div className="text-[11px] text-slate-500 dark:text-slate-400 mb-2 italic">
                  Backend routes dispatch to these names but no implementation exists. UI calls hit a 404. Authoring them is an engine harness task.
                </div>
                <ul className="space-y-1">
                  {plannedEndpoints.map((ep) => {
                    const Icon = ep.status === "implemented" ? CheckCircle2 : CircleDashed;
                    const statusColor = ep.status === "implemented" ? "text-green-600 dark:text-green-400" : "text-amber-600 dark:text-amber-400";
                    return (
                      <li key={ep.name} className="flex items-start gap-2 py-1">
                        <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${statusColor}`} />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="font-mono text-xs text-slate-900 dark:text-slate-100">{ep.name}</span>
                            <span className="text-[9px] px-1 py-0.5 rounded text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30">planned</span>
                          </div>
                          <div className="text-[11px] text-slate-500 dark:text-slate-400">{ep.purpose}</div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null;
            return [...shippedSections, plannedSection].filter(Boolean);
          })()}
        </div>
      )}

      {/* Resize handles */}
      {!isMinimized && (
        <>
          <div onPointerDown={(e) => handleResizeStart(e, "n")} className="absolute top-0 left-2 right-2 h-1 cursor-n-resize" />
          <div onPointerDown={(e) => handleResizeStart(e, "s")} className="absolute bottom-0 left-2 right-2 h-1 cursor-s-resize" />
          <div onPointerDown={(e) => handleResizeStart(e, "w")} className="absolute top-2 bottom-2 left-0 w-1 cursor-w-resize" />
          <div onPointerDown={(e) => handleResizeStart(e, "e")} className="absolute top-2 bottom-2 right-0 w-1 cursor-e-resize" />
          <div onPointerDown={(e) => handleResizeStart(e, "nw")} className="absolute top-0 left-0 w-3 h-3 cursor-nw-resize" />
          <div onPointerDown={(e) => handleResizeStart(e, "ne")} className="absolute top-0 right-0 w-3 h-3 cursor-ne-resize" />
          <div onPointerDown={(e) => handleResizeStart(e, "sw")} className="absolute bottom-0 left-0 w-3 h-3 cursor-sw-resize" />
          <div onPointerDown={(e) => handleResizeStart(e, "se")} className="absolute bottom-0 right-0 w-3 h-3 cursor-se-resize" />
        </>
      )}
    </div>
  );

  // While closed the window is still in the tree (hidden, keeping the session
  // alive) — so the launcher has to render alongside it.
  //
  // ALWAYS the same array shape with STABLE KEYS. Rendering a bare `chatWindow`
  // when open and a fragment `[launcher, chatWindow]` when closed changes the
  // element's position in the child list, so React unmounts and rebuilds the
  // iframe — which silently reintroduced the very bug this fixes (the sid
  // survived via the resume backstop, but the live document, its transcript and
  // any draft did not). The key is what pins the window to one DOM node.
  return createPortal(
    [
      hidden && closedLauncher ? <React.Fragment key="launcher">{closedLauncher}</React.Fragment> : null,
      <React.Fragment key="window">{chatWindow}</React.Fragment>,
    ],
    document.body
  );
}
